import React, { useEffect, useState } from "react";
import { IPage, IPlatform, IPlugin } from "../../types";
import { Section } from "../section/section.comp";
import { StyledDiv } from "./installGuide.styles";
import Image from "next/image";
import { CustomLink } from "../link/link.comp";
import { IoIosArrowForward } from '@react-icons/all-files/io/IoIosArrowForward';
import { CgZoomIn } from '@react-icons/all-files/cg/CgZoomIn';
import { Popup } from "../popup/popup.comp";

interface IInstallGuideProps {
  instructions: any;
  platform: IPlatform;
  app: IPlugin;
  variant?: 'slider' | 'list';
  text?: string;
}

export const InstallGuide = ({
  instructions,
  platform,
  app,
  variant = 'slider',
  text,
}: IInstallGuideProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [zoomedImage, setZoomedImage] = useState(-1);

  const [steps, setSteps] = useState([
    {
      title: "Copy the [app_name] [app_type]’s code.",
      step: `To add Common Ninja’s [app_name] [app_type] to your [platform] [site_type], you have to do the following: 
        First, go to the “Add to Website” tab on the Common Ninja Editor’s dashboard and copy the HTML code.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237137726_image1.png",
    },
    {
      title: "Paste the code to you [platform] [site_type]’s HTML field.",
      step: `Next, go to your [platform] [site_type] and find the HTML field. It is most likely in the form of an HTML/Code widget or module which you can find on [platform]’s dashboard. Add it to your page, and paste the code you’ve copied before to the HTML field. Save and update, and that’s it, you should have Common Ninja’s [app_name] [app_type] on your [platform] [site_type].`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237151468_image2.png",
    },
    {
      title:
        "Alternative Method 1: Through the HTML option in the text’s editing area.",
      step: `Should you have difficulties finding the widget or the module you can use an alternative method of adding Common Ninja’s [app_name] [app_type] to your [platform] [site_type] — adding the code to a special, HTML area in the text editing field.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237160792_image3.png",
    },
    {
      title:
        "Alternative Method 2: Add it directly to your [site_type]’s HTML code.",
      step: `Should you have difficulties finding a widget, or the special area in your text editing field, you can use an alternative method of adding Common Ninja’s [app_name] [app_type] to your [platform] [site_type] — adding it directly to your [site_type]’s HTML code.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237172811_image4.png",
    },
  ]);

  function capitalize(s: string): string {
    if (!s) return "";
    return s[0].toUpperCase() + s.slice(1);
  }

  function replaceWords(s: string) {
    return (s || '')
      ?.replace(/\[app_type\]/g, platform.appSynonym)
      ?.replace(/\[App_type\]/g, capitalize(platform.appSynonym))
      ?.replace(/\[platform\]/g, platform.title)
      ?.replace(/\[site_type\]/g, platform.siteSynonym)
      ?.replace(/\[Site_type\]/g, capitalize(platform.siteSynonym))
      ?.replace(/\[app_name\]/g, app.name);
  }

  useEffect(() => {
    if (instructions.length >= 3) {
      setSteps(instructions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderSteps() {
    if (variant === 'list') {
      return (
        <div className="list">
          <h2>
            {!text ?
              <>How To Add the <span>{app.name}</span> Widget To Your Website</>
              :
              text
            }
          </h2>
          {steps.map((step, idx) => (
            <div key={idx} className="card">
              <div className="slider-text">
                <div className="title-wrapper">
                  <div className="step-dot">
                    {idx + 1 >= 10 ? `${idx + 1}` : `0${idx + 1}`}
                  </div>
                  <h5>
                    {replaceWords(step.title) ||
                      `Step ${idx + 1}`}
                  </h5>
                </div>
                <div
                  className="install-p"
                  dangerouslySetInnerHTML={{
                    __html: replaceWords(step.step),
                  }}
                />
              </div>
              <div onClick={() => setZoomedImage(idx)} className="img-container">
                <CgZoomIn className="zoom" />
                <Image
                  src={step.imgUrl}
                  alt={replaceWords(step.title) || "install steps"}
                  style={{ cursor: 'pointer' }}
                  fill
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <>
        <h2>How to Install</h2>
        <div className="card">
          <div className="text-and-dots">
            <div className="step-dots">
              {steps &&
                steps.map((_, idx) => (
                  <div
                    onClick={() => setActiveStep(idx)}
                    key={`dot-step-${idx + 1}`}
                    className={`step-dot ${activeStep === idx ? "active" : ""}`}
                  >
                    {idx + 1 >= 10 ? `${idx + 1}` : `0${idx + 1}`}
                  </div>
                ))}
            </div>
            <div className="slider-text">
              <h5>
                {replaceWords(steps[activeStep].title) ||
                  `Step ${activeStep + 1}`}
              </h5>
              <div
                className="install-p"
                dangerouslySetInnerHTML={{
                  __html: replaceWords(steps[activeStep].step),
                }}
              />
            </div>
          </div>
          {steps[activeStep].imgUrl && (
            <div className="img-container">
              <Image
                src={steps[activeStep].imgUrl}
                alt={replaceWords(steps[activeStep].title)}
                fill
              />
            </div>
          )}
        </div>
        <CustomLink className="link" variant="secondary" href={`/widgets/${app.slug}/${platform.slug}/how-to-add`}>
          For a Full Guide
          < IoIosArrowForward />
        </CustomLink>
      </>
    );
  }

  return (
    <>
      <Section>
        <StyledDiv>
          {renderSteps()}
        </StyledDiv>
      </Section>
      <Popup
        show={zoomedImage !== -1}
        onClose={() => setZoomedImage(-1)}
        style={{ height: '600px', maxWidth: '95%', width: '1100px', backgroundColor: 'black' }}
      >
        <div className="zoomed-image">
          <Image
            className=""
            src={steps[zoomedImage]?.imgUrl}
            alt={replaceWords(steps[zoomedImage]?.title) || "install steps"}
            fill
            style={{ objectFit: 'contain', borderRadius: '10px' }}
          />
        </div>
      </Popup>
    </>
  );
};
